import React, { useState, useEffect } from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, Alert } from 'react-native';
import { usePlaidLink } from 'react-plaid-link';
import { Colors } from '@/constants/colors';
import { createLinkToken, exchangePublicToken } from '@/services/plaid/plaidService';

interface PlaidLinkButtonProps {
  onSuccess?: (connectionId: string) => void;
  onError?: (error: string) => void;
  buttonText?: string;
  style?: any;
}

export const PlaidLinkButton: React.FC<PlaidLinkButtonProps> = ({
  onSuccess,
  onError,
  buttonText = 'Connect Portfolio',
  style,
}) => {
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  /**
   * Handle success (exchange public token)
   */
  const handleOnSuccess = async (publicToken: string, metadata: any) => {
    console.log('✅ [PlaidLinkButton] Link success, exchanging public token...');
    console.log('Institution:', metadata?.institution?.name);

    try {
      const { data, error } = await exchangePublicToken(publicToken);

      if (error || !data) {
        console.error('❌ [PlaidLinkButton] Failed to exchange token:', error);
        Alert.alert('Error', error || 'Failed to link account');
        onError?.(error || 'Failed to exchange token');
        setLoading(false);
        return;
      }

      console.log(`✅ [PlaidLinkButton] Portfolio connected: ${data.connection_id}`);
      Alert.alert(
        'Success!',
        `Your ${metadata?.institution?.name || 'account'} has been linked successfully.`,
        [{ text: 'OK' }]
      );

      onSuccess?.(data.connection_id);
    } catch (err: any) {
      console.error('❌ [PlaidLinkButton] Unexpected error exchanging token:', err);
      Alert.alert('Error', err.message || 'Failed to link account');
      onError?.(err.message || 'Unexpected error');
    } finally {
      setLoading(false);
      setLinkToken(null);
    }
  };

  /**
   * Handle exit (user cancelled or error)
   */
  const handleOnExit = (error: any, metadata: any) => {
    console.log('🚪 [PlaidLinkButton] Link exited');

    if (error) {
      console.error('❌ [PlaidLinkButton] Link error:', error);
      Alert.alert('Error', error.message || 'Failed to link account');
      onError?.(error.message || 'Link flow failed');
    } else {
      console.log('ℹ️ [PlaidLinkButton] User cancelled link flow');
    }

    setLoading(false);
    setLinkToken(null);
  };

  // Web: Use usePlaidLink hook (always called unconditionally)
  const plaidConfig = usePlaidLink({
    token: linkToken, // null initially, then set after button click
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  });

  // Automatically open Plaid modal when ready on web
  useEffect(() => {
    if (plaidConfig.ready && linkToken) {
      console.log('🌐 [PlaidLinkButton:Web] Opening Plaid Link modal...');
      plaidConfig.open();
    }
  }, [plaidConfig.ready, linkToken]);

  /**
   * Create link token
   */
  const handlePress = async () => {
    setLoading(true);

    try {
      console.log('🔗 [PlaidLinkButton:Web] Creating link token...');
      const { data, error } = await createLinkToken();

      if (error || !data) {
        console.error('❌ [PlaidLinkButton:Web] Failed to create link token:', error);
        Alert.alert('Error', error || 'Failed to initialize Plaid Link');
        onError?.(error || 'Failed to create link token');
        setLoading(false);
        return;
      }

      console.log('✅ [PlaidLinkButton:Web] Link token created');
      setLinkToken(data.link_token);

      // Loading state will be reset by modal opening
      setLoading(false);
    } catch (err: any) {
      console.error('❌ [PlaidLinkButton:Web] Unexpected error:', err);
      Alert.alert('Error', err.message || 'An unexpected error occurred');
      onError?.(err.message || 'Unexpected error');
      setLoading(false);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.button, style, loading && styles.buttonDisabled]}
      onPress={handlePress}
      disabled={loading}
      activeOpacity={0.8}
    >
      {loading ? (
        <ActivityIndicator color="#FFFFFF" size="small" />
      ) : (
        <Text style={styles.buttonText}>{buttonText}</Text>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: Colors.accent.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});
